// Gemini chat client.
// API key comes from REACT_APP_GEMINI_API_KEY (.env.local locally, GitHub secret in CI).
// If the key is missing, chat returns a friendly error instead of crashing.

import { GoogleGenerativeAI } from "@google/generative-ai";

const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

// System instruction defines Exator's assistant voice — ADHD-aware, brief, never preachy.
// Crafted around the symptoms ADHD users actually report: overwhelm, "just do it" advice
// triggering shame, decision fatigue from too many options, etc.
const SYSTEM_INSTRUCTION = `You are Exator's assistant — a warm, ADHD-aware coach in a productivity app.

Voice (user has ADHD):
- Brief: 1–3 short sentences. One thing at a time, never a list of options.
- Acknowledge feeling before advice. Never say "just" or "simply". Don't lecture or explain ADHD.
- Big task → offer the smallest first step. Stuck → smallest possible action.

Understand the user (most apps fail here):
- Expect typos, missing words, no punctuation, autocorrect/voice-to-text errors, shorthand. Infer intent, not literal text ("wrkout 30, reveiw fnance" → workout 30 min, review finances). Never refuse or nitpick spelling.
- Map fuzzy phrasing to the right tool ("keep forgetting to call the dentist" → addReminder; "the proposal is too big" → breakDownTask).
- Missing but inferable detail → pick a sensible default and ACT (date=today unless implied, priority=mild, best-fit category, typical duration). The app shows an editable confirm card, so don't interrogate for what you can reasonably guess.
- Ask ONE short question ONLY if genuinely ambiguous with no safe default; then call no tool. Acting with an editable card beats asking. Be decisive — a mostly-right action they can tweak beats stalling.

Dates: every user message is prefixed [now: <local date/time>]; use it for all relative dates. Output date params as YYYY-MM-DD.

Tool routing:
- addTask: a to-do ("add X", "remind me to Y", "need to Z"); time optional.
- scheduleEvent: calendar-shaped at a specific time (meeting/appointment/call/lesson/class/session/date/party). Specific time + calendar-shaped → prefer this over addTask. Self-directed tasks ("workout 30 min", "read 20 pages") stay addTask even with a time.
- markTaskDone: user finished an EXISTING task ("done with the proposal"). taskName = their short wording.
- breakDownTask: split an EXISTING task into 3–6 short subtasks (≤8 words each). New task → addTask first.
- addReminder: life admin with no fixed hour — bills, birthdays, anniversaries, social check-ins.
- bumpHabit: user did an existing habit-stack item.
- bumpGoalProgress: progress count on a long-term goal (pages read, $ saved). Distinct from markTaskDone.
- setGoalDeadline: move a goal's deadline.
- deleteTask: remove ONE specific task by name.
- deleteTasks: remove SEVERAL at once — "delete all today's tasks", "clear my day / clear the board", "remove everything we just added", "delete the completed ones". The app lists exactly what it will remove and asks the user to confirm, so use THIS (once) instead of many deleteTask calls. "the ones we did today" / "today's tasks" → scope='today'.
- Several requests in one message → a SEPARATE tool call for EACH, same turn; don't merge or do only the first.
- Never ask the user to confirm (the app shows a confirm card). Pure venting/chat/advice → no tool, text only.

Output: no markdown, no bullet lists. One short paragraph or one question.`;

// Function declarations for the AI assistant's tools.
// addTask creates a task entry. scheduleEvent creates a time-blocked calendar event.
// They're related but distinct: tasks are todo items (may or may not have a time);
// events are time-anchored blocks on the schedule.
const TOOLS = {
  functionDeclarations: [
    {
      name: "addTask",
      description:
        "Add a to-do item (with or without a time). Not for vague mentions or general chat.",
      parameters: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description:
              "Short task name as the user would write it (e.g. 'Workout 30 min', 'Email Sarah about proposal').",
          },
          priority: {
            type: "string",
            enum: ["high", "mild", "low"],
            description:
              "Urgency tier. 'high' = must-do-today, 'mild' = important-but-flexible, 'low' = whenever. Default to 'mild' if user didn't say.",
          },
          category: {
            type: "string",
            enum: [
              "Work",
              "Health",
              "Learning",
              "Goal",
              "Free time",
              "Errand",
              "Social",
              "Must-do",
            ],
            description: "Best-fit category. Default to 'Work' if unclear.",
          },
          date: {
            type: "string",
            description:
              "Local date in YYYY-MM-DD format. If user says 'today' use today's date, 'tomorrow' = today+1, etc. Default to today if unspecified.",
          },
          time: {
            type: "string",
            description:
              "Optional time in 24-hour HH:MM format (e.g. '17:00' for 5 PM). Omit entirely if user didn't specify a time.",
          },
          duration: {
            type: "number",
            description:
              "Optional duration in minutes (15, 30, 60, etc). Omit entirely if user didn't specify.",
          },
        },
        required: ["name"],
      },
    },
    {
      name: "markTaskDone",
      description:
        "Mark an existing task done (user finished/completed it). App fuzzy-matches taskName and confirms.",
      parameters: {
        type: "object",
        properties: {
          taskName: {
            type: "string",
            description:
              "The user's reference to the completed task. Use the words they used (e.g. user says 'I finished my workout' → taskName='workout'). Keep it short — just the distinctive part, not the whole sentence.",
          },
        },
        required: ["taskName"],
      },
    },
    {
      name: "breakDownTask",
      description:
        "Split an existing task into smaller subtasks. Existing tasks only — for new tasks use addTask first.",
      parameters: {
        type: "object",
        properties: {
          taskName: {
            type: "string",
            description:
              "The user's reference to the parent task being broken down (e.g. user says 'break down my presentation' → taskName='presentation').",
          },
          subtasks: {
            type: "array",
            items: { type: "string" },
            description:
              "3-6 concrete subtasks. Each should be small enough to start in one short focused session. Order them roughly by sequence (what to do first, then second, etc). Keep each one short — under 8 words ideally.",
          },
        },
        required: ["taskName", "subtasks"],
      },
    },
    {
      name: "bumpGoalProgress",
      description:
        "Increment progress on an existing long-term goal (pages read, money saved, workouts logged).",
      parameters: {
        type: "object",
        properties: {
          goalName: {
            type: "string",
            description:
              "The user's reference to the goal (e.g. user says 'add 20 pages to my reading goal' → goalName='reading'). Short — distinctive word only.",
          },
          amount: {
            type: "number",
            description:
              "How much to add to the current count. Default 1 if user didn't say a number. Must be a positive number.",
          },
        },
        required: ["goalName", "amount"],
      },
    },
    {
      name: "deleteTask",
      description:
        "Remove/cancel/drop ONE item by name — a task OR a scheduled event/calendar entry (NOT the same as marking it done). App fuzzy-matches across all of them and shows a confirm card.",
      parameters: {
        type: "object",
        properties: {
          taskName: {
            type: "string",
            description:
              "The user's reference to the task they want removed (e.g. 'cancel laundry' → taskName='laundry'). Short — distinctive word only.",
          },
        },
        required: ["taskName"],
      },
    },
    {
      name: "deleteTasks",
      description:
        "Remove MULTIPLE items at once (bulk delete) — spans tasks AND scheduled events AND calendar entries. The app resolves the set, shows a confirm card LISTING every item it will remove, and only deletes after the user taps confirm. Use this once instead of many deleteTask calls.",
      parameters: {
        type: "object",
        properties: {
          scope: {
            type: "string",
            enum: ["today", "all-open", "completed", "named"],
            description:
              "'today' = every task scheduled for today (use for 'clear my day' / 'delete today's tasks' / 'the ones we did today'). 'all-open' = every not-done task. 'completed' = done tasks only. 'named' = only the tasks listed in taskNames.",
          },
          taskNames: {
            type: "array",
            items: { type: "string" },
            description:
              "ONLY when scope='named': the specific task references to remove (e.g. ['lunch','workout']). Omit otherwise.",
          },
        },
        required: ["scope"],
      },
    },
    {
      name: "addReminder",
      description:
        "Life-admin reminder (bills, birthdays, anniversaries, social check-ins) with no fixed time block. Not an active to-do.",
      parameters: {
        type: "object",
        properties: {
          title: {
            type: "string",
            description: "Short title for the reminder (e.g. 'Pay rent', 'Mom's birthday', 'Call dentist').",
          },
          when: {
            type: "string",
            description:
              "Human-readable timing as the user said it ('every 1st', 'April 12', 'next Tuesday', 'monthly'). The app stores this as a free-form string — no need to parse into a date.",
          },
          type: {
            type: "string",
            enum: ["bill", "social", "event"],
            description:
              "Reminder kind: 'bill' for payments, 'social' for people-related reminders (birthdays, anniversaries, calls), 'event' for everything else. Default to 'event' if unclear.",
          },
          amount: {
            type: "string",
            description: "Optional dollar amount for bills (e.g. '$120'). Omit for non-bill reminders.",
          },
        },
        required: ["title", "when"],
      },
    },
    {
      name: "bumpHabit",
      description:
        "Mark an existing daily habit-stack item done today (streak +1). App fuzzy-matches and confirms.",
      parameters: {
        type: "object",
        properties: {
          habitName: {
            type: "string",
            description:
              "Distinctive word(s) from the habit — either the trigger ('coffee') or action ('stretch'). User says 'I stretched' → habitName='stretch'.",
          },
        },
        required: ["habitName"],
      },
    },
    {
      name: "setGoalDeadline",
      description:
        "Move an existing long-term goal's deadline.",
      parameters: {
        type: "object",
        properties: {
          goalName: {
            type: "string",
            description:
              "Short distinctive word from the goal name (user says 'push my reading deadline' → goalName='reading').",
          },
          deadline: {
            type: "string",
            description:
              "New deadline in 'MMM YYYY' format (e.g. 'Jun 2026', 'Dec 2026', 'Jun 2027', 'Dec 2027'). The app accepts those four canonical values most cleanly; anything else is a free-form display string.",
          },
        },
        required: ["goalName", "deadline"],
      },
    },
    {
      name: "scheduleEvent",
      description:
        "Add a time-blocked calendar event (meeting/appointment/time-anchored activity). General to-dos → addTask.",
      parameters: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description: "Event name (e.g. 'Doctor appointment', 'Call with Mom').",
          },
          date: {
            type: "string",
            description:
              "Local date in YYYY-MM-DD format. Default to today if unspecified.",
          },
          time: {
            type: "string",
            description:
              "Start time in 24-hour HH:MM format (e.g. '15:00' for 3 PM). REQUIRED for events.",
          },
          duration: {
            type: "number",
            description:
              "Duration in minutes. Default to 30 if user didn't specify.",
          },
          category: {
            type: "string",
            enum: ["work", "mustdo", "social", "personal", "health", "free", "life"],
            description:
              "Schedule category (lowercase). work=meetings/work blocks, social=people stuff, personal=appointments/errands, health=workout/wellness, free=breaks, life=bills/admin. Default 'personal' if unclear.",
          },
          location: {
            type: "string",
            description: "Optional location (e.g. 'Zoom', 'Dr. Office', 'Starbucks'). Omit if not mentioned.",
          },
        },
        required: ["name", "time"],
      },
    },
  ],
};

let model = null;
if (apiKey) {
  const genAI = new GoogleGenerativeAI(apiKey);
  // gemini-2.5-flash is the current GA flash model with the broadest free-tier availability
  // across new API keys. Older `gemini-1.5-flash` returns 404 on accounts created after
  // Google's 2025 model rollout. 2.5-flash supports tools/function-calling and matches the
  // same free-tier quota.
  model = genAI.getGenerativeModel({
    model: "gemini-2.5-flash",
    systemInstruction: SYSTEM_INSTRUCTION,
    tools: [TOOLS],
    // Hard cap on reply length — replies are 1–3 sentences + small function args,
    // so this never truncates real output but stops runaway/costly generations.
    generationConfig: { maxOutputTokens: 800 },
  });
}

// Vision model — no tools/function-calling, just multimodal understanding. Used by the
// Exator Assistant camera: the user snaps a to-do list OR an event flyer and we let the
// model decide which it is and extract structured data.
let visionModel = null;
if (apiKey) {
  const genAI = new GoogleGenerativeAI(apiKey);
  visionModel = genAI.getGenerativeModel({ model: "gemini-2.5-flash", generationConfig: { maxOutputTokens: 1024 } });
}

// Analyze a photo. base64 is the raw base64 (no data: prefix); mimeType like "image/jpeg".
// Returns one of:
//   { ok:true, kind:"tasks", tasks:[{name,priority,category}], note }
//   { ok:true, kind:"event", event:{name,date,time,durationMin,location}, note }
//   { ok:true, kind:"text", note }              // couldn't extract anything actionable
//   { ok:false, error }
// The model is told to self-classify list-vs-flyer so the UI doesn't have to ask.
export async function analyzeImageWithGemini(base64, mimeType = "image/jpeg") {
  if (!visionModel) {
    return { ok: false, error: "AI vision isn't configured (missing API key)." };
  }
  const todayIso = new Date().toISOString().slice(0, 10);
  const prompt = `You are Exator's vision helper. The user photographed something. Decide what it is and respond with ONLY a JSON object, no markdown fences, no prose.

If it's a TO-DO / TASK LIST (handwritten or printed):
{"kind":"tasks","tasks":[{"name":"short task","priority":"high|mild|low","category":"Work|Health|Learning|Goal|Free time|Errand|Social|Must-do"}],"note":"one friendly sentence"}

If it's an EVENT FLYER / POSTER / INVITE (a single dated happening):
{"kind":"event","event":{"name":"event name","date":"YYYY-MM-DD","time":"HH:MM 24h or empty","durationMin":120,"location":"venue or empty"},"note":"one friendly sentence"}

If you can't extract anything actionable:
{"kind":"text","note":"what you see, one sentence"}

Today is ${todayIso}. Resolve relative dates ("this Saturday") against it. Max 12 tasks. Keep names short. Output raw JSON only.`;
  try {
    const result = await visionModel.generateContent([
      { text: prompt },
      { inlineData: { data: base64, mimeType } },
    ]);
    const u = result.response.usageMetadata;
    if (u) console.log(`[gemini vision] tokens prompt:${u.promptTokenCount} out:${u.candidatesTokenCount} total:${u.totalTokenCount}`);
    let raw = (result.response.text() || "").trim();
    // Strip accidental ```json fences if the model adds them anyway.
    raw = raw.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();
    const parsed = JSON.parse(raw);
    return { ok: true, ...parsed };
  } catch (e) {
    console.error("[vision] failed:", e);
    const m = (e?.message || "").toLowerCase();
    const friendly = (m.includes("429") || m.includes("quota") || m.includes("rate"))
      ? "I'm getting a lot of requests right now — try the photo again in a moment."
      : "I couldn't read that image — try a clearer, well-lit shot.";
    return { ok: false, error: friendly };
  }
}

// Build a one-line context string the model uses to ground its replies in the user's actual state.
// Kept short on purpose — too much context can dilute the model's focus.
function formatUserContext({ tasks = [], onboardingMode, energy, totalWins }) {
  const parts = [];
  if (onboardingMode) parts.push(`mode: ${onboardingMode}`);
  if (energy) parts.push(`energy: ${energy}`);

  const open = tasks.filter((t) => !t.done);
  if (open.length === 0) {
    parts.push("no open tasks");
  } else {
    parts.push(`${open.length} open task${open.length === 1 ? "" : "s"}`);
    const top =
      open.find((t) => t.priority === "high") ||
      open.find((t) => t.priority === "mild") ||
      open[0];
    if (top) parts.push(`top: "${top.name}"`);
  }

  if (totalWins > 0) parts.push(`${totalWins} wins all-time`);
  return parts.join(", ");
}

// Map raw Gemini/network errors to calm, ADHD-tone messages. Full technical detail
// still goes to the console — users never see scary raw API error text.
function friendlyAiError(err) {
  const msg = (err?.message || String(err) || "").toLowerCase();
  const code = err?.code || err?.status || "";
  if (msg.includes("429") || msg.includes("quota") || msg.includes("rate limit") || code === 429)
    return "I'm getting a lot of requests right now — give me a moment and try again.";
  if (msg.includes("api key") || msg.includes("api_key") || msg.includes("permission") || code === 401 || code === 403)
    return "My AI connection isn't set up right at the moment. Try again shortly.";
  if (msg.includes("network") || msg.includes("fetch") || msg.includes("timeout") || msg.includes("unavailable") || msg.includes("503") || msg.includes("500") || code === 503 || code === 500)
    return "I can't reach the assistant right now — check your connection and try again.";
  return "Something glitched on my end. Try that again in a moment.";
}

// Retry once on transient network/5xx blips only — NOT on 429 (retrying a quota
// wall just burns another request against the cap, which is the opposite of helpful).
function isTransient(err) {
  const msg = (err?.message || String(err) || "").toLowerCase();
  const code = err?.code || err?.status || "";
  if (msg.includes("429") || msg.includes("quota") || code === 429) return false;
  return (
    msg.includes("network") || msg.includes("fetch") || msg.includes("timeout") ||
    msg.includes("unavailable") || msg.includes("503") || msg.includes("500") ||
    code === 503 || code === 500
  );
}

/**
 * Send a chat message to Gemini and get a reply.
 * Reply is either a plain text response OR a structured function call (e.g. addTask).
 *
 * @param {string} message - the new user message
 * @param {Array<{f:'u'|'a', t:string}>} history - previous messages in app's format
 * @param {object} userContext - tasks/mode/energy/etc to ground the reply
 * @returns {Promise<{ok:boolean, type:'text'|'function_call'|'error', text:string, functionCall?:{name:string,args:object}}>}
 */
export async function chatWithGemini(message, history = [], userContext = {}) {
  if (!model) {
    return {
      ok: false,
      type: "error",
      text:
        "AI chat isn't configured yet. (Dev: add REACT_APP_GEMINI_API_KEY to .env.local and restart npm start.)",
    };
  }

  try {
    // Convert app's history format to Gemini's. Last 6 messages (~3 turns) is enough
    // context; keeping it short controls tokens and prevents stale conversation drift.
    // Filter out function-call messages from history — they aren't part of the conversational thread.
    const geminiHistory = history
      .slice(-6)
      .filter((m) => m.t)
      .map((m) => ({
        role: m.f === "u" ? "user" : "model",
        parts: [{ text: m.t }],
      }));
    // Gemini requires history to START with a 'user' turn. After slicing/filtering,
    // the window can begin on an assistant reply — drop leading model turns to avoid
    // "First content should be with role 'user', got model".
    while (geminiHistory.length && geminiHistory[0].role === "model") geminiHistory.shift();

    const chat = model.startChat({ history: geminiHistory });

    // Prepend bracketed context. LLMs have NO built-in clock, so without this Gemini
    // doesn't know what "today" or "tomorrow" mean and guesses based on training data —
    // which is why scheduling "tomorrow at 10am" was landing on wrong dates.
    // We give it the local date in three forms (weekday name, ISO, plus current time)
    // so it can compute relative phrases ("Friday", "next week", "in two days") correctly.
    const now = new Date();
    const todayWeekday = now.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const todayIso =
      now.getFullYear() +
      "-" +
      String(now.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(now.getDate()).padStart(2, "0");
    const nowTime = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    const ctx = formatUserContext(userContext);
    const dateLine = `[now: ${todayWeekday} (${todayIso}) at ${nowTime}]`;
    const stateLine = ctx ? `[user state: ${ctx}]` : "";
    const fullMessage = [dateLine, stateLine, "", message].filter(Boolean).join("\n");

    // Single retry on transient network/5xx blips only (isTransient excludes 429).
    let result;
    try {
      result = await chat.sendMessage(fullMessage);
    } catch (e1) {
      if (!isTransient(e1)) throw e1;
      await new Promise((r) => setTimeout(r, 800));
      result = await chat.sendMessage(fullMessage);
    }
    const response = result.response;
    const u = response.usageMetadata;
    if (u) console.log(`[gemini chat] tokens prompt:${u.promptTokenCount} out:${u.candidatesTokenCount} total:${u.totalTokenCount}`);

    // Check if Gemini decided to call a function instead of replying with text.
    const calls = response.functionCalls();
    if (calls && calls.length > 0) {
      // Some calls come with text alongside the call (model commenting before the tool use).
      // Use that as the chat bubble; if empty, provide a sensible default.
      let preamble = "";
      try {
        preamble = response.text().trim();
      } catch (_) {
        preamble = "";
      }
      // Return ALL calls so a single message like "add X, Y and Z" yields one
      // confirm card per item. functionCall (singular) kept for back-compat.
      const mapped = calls.map((c) => ({ name: c.name, args: c.args }));
      return {
        ok: true,
        type: "function_call",
        text: preamble || "I'll add that for you.",
        functionCall: mapped[0],
        functionCalls: mapped,
      };
    }

    return { ok: true, type: "text", text: response.text().trim() };
  } catch (err) {
    // Full technical detail to the console for debugging; the user sees a calm message.
    console.error("Gemini chat error:", err);
    return { ok: false, type: "error", text: friendlyAiError(err) };
  }
}
